import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState } from "react"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { MultiSelectDropdown, Table, Toggle } from "@/components"
import { CarrierType, PortOfEntryType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import Swal from "sweetalert2"
import { convertEquipmentType } from "@/lib/services"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: CarrierType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: CarrierType) => void
  handleFormUpdate: (form: CarrierType) => void
  handleFormDelete: (form: string) => void
  portOfEntriesListing: { data: PortOfEntryType[] }
  title: string
  setTitle: (title: string) => void
}

export const Carrier: FC<Props> = (props) => {
  const [CTPAT, setCTPAT] = useState(false)
  const [hazmat, setHazmat] = useState(false)
  const [carrier, setCarrier] = useState(false)
  const [transload, setTransload] = useState(false)
  const [onboarded, setOnboarded] = useState(false)
  const [portOfEntries, setPortOfEntries] = useState<
    { id: string; name: string }[]
  >([])
  const [selectedPortOfEntry, setSelectedPortOfEntry] = useState<
    { id: string; name: string }[]
  >([])

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CarrierType>()

  const onSubmit = (data: CarrierType) => {
    data.onboarded = onboarded
    data.CTPAT = CTPAT
    data.hazmat = hazmat
    data.transload = transload
    data.carrier = carrier
    data.USDOT = Number(data.USDOT)
    const portOfEntryId = portOfEntries.map((portOfEntry) => portOfEntry.id)

    data.portOfEntry = [...portOfEntryId]
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      // color: "#ffffff",
      // background: "#333333",
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: CarrierType) => {
    props.setTitle("Update Carrier Network")
    setValue("id", e.id)
    setValue("name", e.name)
    setValue("USDOT", e.USDOT == null ? 0 : e.USDOT)
    setValue("MCMX", e.MCMX == "" ? "-" : e.MCMX)
    setValue("contactName", e.contactName == null ? "-" : e.contactName)
    setValue("email", e.email == null ? "-" : e.email)
    setValue("phone", e.phone == null ? "-" : e.phone)
    setValue("equipment", e.equipment)
    setOnboarded(e.onboarded)
    setCTPAT(e.CTPAT)
    setHazmat(e.hazmat)
    setTransload(e.transload)
    setCarrier(e.carrier)
    const selectedPortOfEnteries = e.portOfEntries.filter(
      (entry) => entry != undefined && e.id,
    )
    setSelectedPortOfEntry(
      selectedPortOfEnteries as { id: string; name: string }[] | [],
    )
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      name: "",
      USDOT: undefined,
      MCMX: "",
      contactName: "",
      email: "",
      phone: "",
      equipment: "",
      hazmat: false,
      transload: false,
      carrier: false,
      id: undefined,
    })
    setOnboarded(false)
    setCTPAT(false)
    setHazmat(false)
    setTransload(false)
    setCarrier(false)
    setPortOfEntries([])
    setSelectedPortOfEntry([])
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="medium"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Name
                </label>
                <input
                  className={
                    (errors.name
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Name"
                  {...register("name", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  USDOT
                </label>
                <input
                  className={
                    (errors.USDOT
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter USDOT"
                  {...register("USDOT", { required: true })}
                />
              </fieldset>
            </div>
            <div className="grid md:grid-cols-2 grid-cols-1 md:gap-2 gap-3">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  MCMX
                </label>
                <input
                  className={
                    (errors.MCMX
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter MCMX"
                  {...register("MCMX", { required: true })}
                />
              </fieldset>
              <div className="flex justify-between">
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">
                    Onboarded
                  </label>
                  <Toggle
                    toggled={onboarded}
                    onClick={(state: boolean) => setOnboarded(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">
                    CTPAT
                  </label>
                  <Toggle
                    toggled={CTPAT}
                    onClick={(state: boolean) => setCTPAT(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">
                    Hazmat
                  </label>
                  <Toggle
                    toggled={hazmat}
                    onClick={(state: boolean) => setHazmat(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">
                    Transload
                  </label>
                  <Toggle
                    toggled={transload}
                    onClick={(state: boolean) => setTransload(state)}
                  />
                </div>
                <div className="flex flex-col justify-evenly pl-1">
                  <label className="text-xs dark:text-primary-8 text-light-1">
                    Carrier
                  </label>
                  <Toggle
                    toggled={carrier}
                    onClick={(state: boolean) => setCarrier(state)}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Contact Name
                </label>
                <input
                  className={
                    (errors.contactName
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter ContactName"
                  {...register("contactName", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Email
                </label>
                <input
                  className={
                    (errors.email
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="email"
                  placeholder="Enter Email"
                  {...register("email", { required: true })}
                />
              </fieldset>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col ">
                <fieldset>
                  <label className="text-xs dark:text-primary-8 text-light-1">
                    Equipment
                  </label>
                  <input
                    className={
                      (errors.equipment
                        ? "border-red-400"
                        : "dark:border-secondary-2") +
                      " w-full text-sm p-2 mt-1 outline-none border rounded drop-shadow dark:bg-primary-1 bg-white text-light-2 dark:text-primary-2 placeholder:text-zinc-500 placeholder:text-xs"
                    }
                    placeholder="Enter Equipment"
                    {...register("equipment", { required: true })}
                  />
                </fieldset>
                <fieldset>
                  <label className="text-xs dark:text-primary-8 text-light-1">
                    Phone
                  </label>
                  <input
                    className={
                      (errors.phone
                        ? "border-red-400"
                        : "dark:border-secondary-2") +
                      " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                    }
                    type="text"
                    placeholder="Enter Phone"
                    {...register("phone", { required: true })}
                  />
                </fieldset>
              </div>
              <MultiSelectDropdown
                title="Port Of Entries"
                selectedList={portOfEntries}
                setSelectedList={setPortOfEntries}
                selectedIds={selectedPortOfEntry}
                listingData={props.portOfEntriesListing}
              />
            </div>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-80"}>{e.name}</td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                {e.USDOT == null ? "-" : e.USDOT}
              </td>
              <td className={"whitespace-nowrap p-4 w-28"}>
                {e.MCMX == "" ? "-" : e.MCMX}
              </td>
              <td className={"whitespace-nowrap p-4 w-32"}>
                <div className="flex justify-center">
                  {e.onboarded ? (
                    <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                  ) : (
                    <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                  )}
                </div>
              </td>
              <td className={"whitespace-nowrap p-4 w-40"}>
                {e.contactName == null ? "-" : e.contactName}
              </td>
              <td className={"whitespace-nowrap p-4 w-80"}>
                {e.email == null ? "-" : e.email}
              </td>
              <td className={"whitespace-nowrap p-4 w-28"}>
                {e.phone == null ? "-" : e.phone}
              </td>
              <td className={"whitespace-nowrap p-4 w-72"}>
                <p>{convertEquipmentType(e.equipment)}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-20"}>
                <div className="flex justify-center">
                  {e.CTPAT ? (
                    <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                  ) : (
                    <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                  )}
                </div>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-center">
                  {e.hazmat ? (
                    <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                  ) : (
                    <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                  )}
                </div>
              </td>
              <td className={"whitespace-nowrap p-4 w-28"}>
                <div className="flex justify-center">
                  {e.transload ? (
                    <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                  ) : (
                    <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                  )}
                </div>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-center">
                  {e.carrier ? (
                    <CheckCircleIcon className="w-7 h-7 text-green-600 cursor-pointer" />
                  ) : (
                    <ExclamationCircleIcon className="w-7 h-7 text-red-600 cursor-pointer" />
                  )}
                </div>
              </td>
              <td className="whitespace-nowrap p-4 w-24 flex justify-end items-center gap-2">
                <PencilSquareIcon
                  className="w-4 h-4 text-accent cursor-pointer"
                  onClick={() => populateData(e)}
                />
                <TrashIcon
                  className="w-4 h-4 text-accent cursor-pointer"
                  onClick={() => onDelete(e.id)}
                />
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
